import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Handling Secrets",
  "description": "Talking to 3rd party APIs is where Serverless shines. Which of the 3 ways to handle secrets should you choose?",
  "image": "./img/handling-secrets.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "handling-secrets"
    }}>{`Handling Secrets`}</h1>
    <p><img parentName="p" {...{
        "src": "/63aa06dc10a6d49fa7aac1f416effb12/handling-secrets.svg",
        "alt": null
      }}></img></p>
    <p>{`How do you send an SMS when users click a button?`}</p>
    <p>{`You find a JavaScript library that talks to an SMS provider. Configure your API keys, call the library, user gets an SMS. Yay!`}</p>
    <p>{`3 months later you wake up to a $5,000 bill. Someone looked at your JavaScript code, took the API keys, and ran a spam campaign.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/mvyvXwL26FfAtRCLPk/giphy-loop.mp4?cid=4ac046a2l8fv3mbtqw10qri3us3eqp4jqp3mkmx5tfgeiaae&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Orchestrating 3rd party services is where cloud functions shine. The perfect environment for glue code.`}</p>
    <p>{`Isolated code that does `}<em parentName="p">{`one`}</em>{` thing with no cruft. Runs on-demand, consumes no resources when not in use, scales near infinitely. Perfection.`}</p>
    <p><strong parentName="p">{`And it runs on a server where users can't see the code.`}</strong>{` There's no right-click inspect, no JavaScript files downloaded, no user environment at all.`}</p>
    <p>{`😍`}</p>
    <h2 {...{
      "id": "what-is-a-secret"
    }}>{`What is a secret`}</h2>
    <p>{`A secret is any piece of information you can't share. Any key with access to a special resource. Passwords and API tokens, for example. `}</p>
    <p>{`You can add semi-secret configuration variables. URLs for parts of your system, ports of a database server, kinda-hardcoded data, etc.`}</p>
    <p>{`How secretive you have to be depends on context.`}</p>
    <p>{`Configuration variables are okay to leak, if the system is otherwise secure. But they can give an attacker information about your system.`}</p>
    <p>{`Production passwords for sensitive health information ... you don't even want your engineers to know those. Especially not former engineers.`}</p>
    <h2 {...{
      "id": "3-ways-to-handle-secrets"
    }}>{`3 ways to handle secrets`}</h2>
    <p>{`There are 3 ways to handle secrets. From least to most secure.`}</p>
    <ol>
      <li parentName="ol">{`Hardcoded values`}</li>
      <li parentName="ol">{`Dotenv files`}</li>
      <li parentName="ol">{`Secrets manager`}</li>
    </ol>
    <p>{`Each method comes with different pros and cons. Pros in terms of security, cons in how cumbersome to use.`}</p>
    <div id="lock" />
    <h2 {...{
      "id": "hardcoded-values-in-code"
    }}>{`Hardcoded values in code`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`MY_SECRET_KEY="f3q20-98facv87432q4"
`}</code></pre>
    <p>{`Hardcoded secrets are the easiest to use and the least secure.`}</p>
    <p>{`They're okay for prototyping. Reduce moving pieces and focus on the API integration. Ignore the yak shaving around your goal.`}</p>
    <p>{`Code runs on the server and users won't be able to steal your secrets.`}</p>
    <p><strong parentName="p">{`But anyone with access to your code can see the secrets.`}</strong>{` `}</p>
    <p>{`Share on GitHub and that includes the whole world. Bots always scrape GitHub looking for strings that look like keys. Your secret `}<em parentName="p">{`will`}</em>{` be stolen.`}</p>
    <p>{`AWS is paranoid enough that their own bot looks for secret keys. If they find yours, your AWS account gets locked. `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/what-happens-when-you-push-aws-credentials-to-github/"
      }}>{`Ask me how I know`}</a>{` 😅`}</p>
    <p>{`Another issue with hardcoded keys is that they're hard to change. You have to re-deploy every time. And you're forced to use the same account for testing and production.`}</p>
    <h2 {...{
      "id": "dotenv-files"
    }}>{`Dotenv files`}</h2>
    <p>{`A step up from hardcoded keys are dotenv files – `}<inlineCode parentName="p">{`.env`}</inlineCode>{`. Configuration files in your codebase that hold secrets.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`# .env
MY_SECRET_KEY=f3q20-98facv87432q4
MY_API_URL=https://example.com
`}</code></pre>
    <p>{`A `}<inlineCode parentName="p">{`.env`}</inlineCode>{` file holds your secrets and configuration variables in one place. Makes them easier to use and change without searching through code.`}</p>
    <p><strong parentName="p">{`You should `}<em parentName="strong">{`not`}</em>{` store these in version control.`}</strong>{` That's where the increased security comes from. `}</p>
    <p>{`The common approach is to:`}</p>
    <ol>
      <li parentName="ol">{`Have a blank `}<inlineCode parentName="li">{`.env`}</inlineCode>{` file with every variable stored in version control`}</li>
      <li parentName="ol">{`Every engineer makes a copy`}</li>
      <li parentName="ol">{`Fills out values from team members or a shared passwords manager`}</li>
    </ol>
    <p>{`You'll never leak secrets to GitHub by accident. But they're unencrypted on everyone's laptop, difficult to change across a large team, and packaged into your deploys.`}</p>
    <p>{`Anyone who breaks into your laptop or steals a deploy package from S3 can read the secrets.`}</p>
    <p>{`On the bright side, dotenv files are easy to split between environments. You can have `}<inlineCode parentName="p">{`.env.local`}</inlineCode>{`, `}<inlineCode parentName="p">{`.env.production`}</inlineCode>{`, `}<inlineCode parentName="p">{`.env.development`}</inlineCode>{` with different values for every secret. ✌️`}</p>
    <h3 {...{
      "id": "how-to-use-env-files"
    }}>{`How to use .env files`}</h3>
    <p>{`Many frameworks support `}<inlineCode parentName="p">{`.env`}</inlineCode>{` files by default. Populate the file and read values from `}<inlineCode parentName="p">{`process.env`}</inlineCode>{`.`}</p>
    <p>{`When using the Serverless Framework, you'll need a plugin: `}<inlineCode parentName="p">{`serverless-dotenv-plugin`}</inlineCode>{`. Here's what you do.`}</p>
    <p>{`Install the plugin:`}</p>
    <pre><code parentName="pre" {...{}}>{`yarn add serverless-dotenv-plugin
`}</code></pre>
    <p>{`Enable it in your serverless.yml config:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`# serverless.yml
plugins:
  - serverless-dotenv-plugin
`}</code></pre>
    <p>{`Run deploy and access values with `}<inlineCode parentName="p">{`process.env`}</inlineCode>{` 🤘`}</p>
    <p>{`You can match environment specific files to deployments using the `}<inlineCode parentName="p">{`stage: X`}</inlineCode>{` config. serverless-dotenv-plugin reads the `}<inlineCode parentName="p">{`.env.X`}</inlineCode>{` file that matches your stage.`}</p>
    <h2 {...{
      "id": "secrets-manager"
    }}>{`Secrets manager`}</h2>
    <p>{`The most secure way to handle secrets is using a secrets manager.`}</p>
    <p>{`A secrets manager works like the password manager in your browser. You have to authenticate to get access, re-authenticate `}<em parentName="p">{`every time`}</em>{`, and secrets are encrypted when not in use.`}</p>
    <p>{`You can even make your secrets double blind. `}<em parentName="p">{`Nobody`}</em>{` needs to know their values.`}</p>
    <p>{`Engineers can't see secrets in the code, they're not saved on anyone's laptop, you can't steal them from the server, and with the right configuration, secrets change every N days.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/3o6ZsYhBJLkgnCE9vq/giphy-loop.mp4?cid=4ac046a20iwmqdolpn39a81ihee975i1e1boce7bljwom7eu&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h3 {...{
      "id": "how-to-use-a-secrets-manager"
    }}>{`How to use a secrets manager`}</h3>
    <p>{`If you're on Netlify or Vercel, their secrets system is a secrets manager. They control the run-time and inject those values into `}<inlineCode parentName="p">{`process.env`}</inlineCode>{`.`}</p>
    <p>{`On AWS, you'll have to partially build your own.`}</p>
    <p><strong parentName="p">{`First`}</strong>{`, save your secrets in `}<a parentName="p" {...{
        "href": "https://console.aws.amazon.com/secretsmanager/home"
      }}>{`AWS Secrets Manager`}</a>{`. Follow the wizard, it's great.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/29db8966f2585b94cf27a155979072c7/49b61/secrets-manager.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "36.77130044843049%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'146\\'%20viewBox=\\'0%200%20400%20146\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M2%2059l38%201%2037-1-37-1-38%201m71%2042h62v14H12l-1-7-1-7v7l1%208h125v-16H11l62%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/29db8966f2585b94cf27a155979072c7/ca0a1/secrets-manager.webp 223w", "/static/29db8966f2585b94cf27a155979072c7/75680/secrets-manager.webp 445w", "/static/29db8966f2585b94cf27a155979072c7/8d1ba/secrets-manager.webp 890w", "/static/29db8966f2585b94cf27a155979072c7/3838e/secrets-manager.webp 1335w", "/static/29db8966f2585b94cf27a155979072c7/3afba/secrets-manager.webp 1756w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/29db8966f2585b94cf27a155979072c7/e92b6/secrets-manager.png 223w", "/static/29db8966f2585b94cf27a155979072c7/e66bf/secrets-manager.png 445w", "/static/29db8966f2585b94cf27a155979072c7/4ef49/secrets-manager.png 890w", "/static/29db8966f2585b94cf27a155979072c7/4e814/secrets-manager.png 1335w", "/static/29db8966f2585b94cf27a155979072c7/49b61/secrets-manager.png 1756w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/29db8966f2585b94cf27a155979072c7/4ef49/secrets-manager.png",
                "alt": "AWS Secrets Manager configuration",
                "title": "AWS Secrets Manager configuration",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`AWS Secrets Manager configuration`}</figcaption>{`
  `}</figure></p>
    <p>{`You can store many secret values in 1 configuration. I recommend grouping by environment – dev, staging, production – or use a logical grouping based on what you're doing. One secret per API.`}</p>
    <p><strong parentName="p">{`Second`}</strong>{`, give your code permission to access secrets.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`# serverless.yml
provider:
  # ...
  iamRoleStatements:
    - Effect: "Allow"
      Action:
        - "secretsmanager:GetSecretValue"
      Resource: "arn:aws:secretsmanager:\${self:provider.region}:*"
`}</code></pre>
    <p>{`You're giving permission to `}<inlineCode parentName="p">{`GetSecretValue`}</inlineCode>{`, `}<em parentName="p">{`not`}</em>{` to make changes. This is important. You do not want somebody hacking into your system and locking you out.`}</p>
    <p>{`Using an asterisk – `}<inlineCode parentName="p">{`*`}</inlineCode>{` – for secret name is convenient. For more security, limit access to specific secrets.`}</p>
    <p><strong parentName="p">{`Third`}</strong>{`, access your secrets at runtime.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { SecretsManager } from "aws-sdk"

const ssm = new SecretsManager({
  region: "us-east-1", // make sure this matches your region
})

const secret = await ssm
  .getSecretValue({ SecretId: "<your secret name>" })
  .promise()

const { MY_SECRET_KEY, MY_API_URL } = JSON.parse(secret?.SecretString)
`}</code></pre>
    <p>{`This instantiates a new SSM client, gets your secret value, returns a JSON. Parse JSON, get secrets.`}</p>
    <p>{`This is `}<a parentName="p" {...{
        "href": "https://serverlesshandbook.dev/robust-backend-design"
      }}>{`an API call that might fail`}</a>{`. Make sure to handle errors and fail correctly, if you can't get the secret.`}</p>
    <p>{`You'll have to do this every time.`}</p>
    <p>{`Fetching a fresh secret every time ensures that:`}</p>
    <ol>
      <li parentName="ol">{`you get the latest value`}</li>
      <li parentName="ol">{`you don't keep decrypted secrets in memory for long`}</li>
    </ol>
    <p>{`However, this increases latency. You're making an API call. Not a lot, but not zero. `}</p>
    <p>{`You can memoize secrets values in Lambda memory. Rely on the ephemeral nature of your environment to forget.`}</p>
    <h2 {...{
      "id": "conclusion"
    }}>{`Conclusion`}</h2>
    <p>{`Choose the strategy that fits your use-case and safety needs.`}</p>
    <p>{`I like to hardcode development values in the tinkering phase and `}<em parentName="p">{`change keys afterwards`}</em>{`. You never know what you leaked.`}</p>
    <p>{`When my code's ready, I put configuration values in `}<inlineCode parentName="p">{`.env`}</inlineCode>{` files and secrets in Secrets Manager.`}</p>
    <p>{`Next chapter we look at authentication.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      